import React, { useMemo } from 'react';
import ChartCard, {
    ChartCardContent as CardContent,
    ChartCardHeader as CardHeader,
    ChartCardTitle as CardTitle,
} from '../charts/ChartCard';
import EChart from '../charts/EChart';
import { useChartPalette } from '../charts/useChartMode';
import { smoothLineOption, withAlpha } from '../charts/options';

interface TrendPoint {
    date: string;
    totalAvails: number;
    brandSafeUrls: number;
}

interface HistoricalTrendsChartProps {
    data: TrendPoint[];
    loading?: boolean;
}

// Pale enough that the impressions line stays the foreground — the volume-bar
// wash weight shared with Job Performance.
const VOLUME_BAR_ALPHA = 0.25;

const HistoricalTrendsChart: React.FC<HistoricalTrendsChartProps> = ({ data, loading }) => {
    return (
        <ChartCard>
            <CardHeader>
                <CardTitle>Availability Trends</CardTitle>
            </CardHeader>
            <CardContent>
                {loading ? <LoadingSkeleton /> : <ChartBody data={data} />}
            </CardContent>
        </ChartCard>
    );
};

function ChartBody({ data }: { data: TrendPoint[] }) {
    const palette = useChartPalette();
    const lineColor = palette.categorical[0];
    const barColor = palette.categorical[1];

    const option = useMemo(
        () =>
            smoothLineOption({
                palette,
                categories: data.map((d) => formatDate(d.date)),
                valueFormatter: shortNumber,
                // The two measures are orders of magnitude apart, so brand-safe
                // URLs ride the volume layer — its own right-hand scale, drawn
                // as bars behind the line — rather than a shared axis that
                // would flatten one of them.
                series: [
                    {
                        name: 'Total Impressions',
                        data: data.map((d) => d.totalAvails),
                        color: lineColor,
                        area: true,
                    },
                ],
                volumes: [
                    {
                        name: 'Brand-Safe URLs',
                        data: data.map((d) => d.brandSafeUrls),
                        colors: data.map(() => withAlpha(barColor, VOLUME_BAR_ALPHA)),
                        axisColor: barColor,
                        valueFormatter: plainCount,
                    },
                ],
                // The scale wording lives in the caption below the chart, which
                // reads better in this card's narrow two-thirds column than an
                // ECharts legend row eating into the plot height.
                showLegend: false,
            }),
        [data, palette, lineColor, barColor],
    );

    if (data.length === 0) {
        return (
            <div className="h-64 flex items-center justify-center text-sm text-ink-muted">
                No historical data available
            </div>
        );
    }

    return (
        <>
            <EChart option={option} height={260} ariaLabel="Availability trends over time" />
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
                <CaptionKey color={lineColor} label="Total Impressions (left scale)" />
                <CaptionKey color={barColor} label="Brand-Safe URLs (bars, right scale)" />
            </div>
        </>
    );
}

function CaptionKey({ color, label }: { color: string; label: string }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
            <span>{label}</span>
        </span>
    );
}

function LoadingSkeleton() {
    return <div className="h-64 bg-canvas rounded-md animate-pulse" />;
}

function formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function shortNumber(value: number): string {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
    return String(value);
}

// Pinned to en-US so the right-hand counts keep their comma grouping wherever
// the page (or a test runner) is running.
const countFormat = new Intl.NumberFormat('en-US');

function plainCount(value: number): string {
    return countFormat.format(value);
}

export default HistoricalTrendsChart;
